import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { getReceipt, getTransaction, listReceiptAnalyses, listReceipts, listUnattachedReceipts, type Receipt } from '../../../types'
import { actionApiCall } from '../api/actionApiCall'

export const receiptsQueryKey = (transactionId: string) => ['receipts', transactionId] as const
export const transactionQueryKey = (transactionId: string) => ['transaction', transactionId] as const
export const receiptQueueQueryKey = (groupId: string) => ['receipts', 'fronta', groupId] as const
export const receiptQueryKey = (receiptId: string) => ['receipts', 'detail', receiptId] as const

/** Běží u účtenky rozpoznávání? */
export function seRozpoznava(receipt: Receipt): boolean {
    return receipt.analysisStatus === 'PENDING' || receipt.analysisStatus === 'RUNNING'
}

/**
 * Čeká účtenka, až ji zpracuje AI asistent přes MCP? U volby „obojí" může
 * současně běžet i rozpoznávání na serveru.
 */
export function cekaNaAsistenta(receipt: Receipt): boolean {
    return receipt.awaitingMcp || receipt.analysisStatus === 'AWAITING_MCP'
}

/**
 * Mají se údaje z účtenky teprve objevit - rozpoznává ji server, nebo čeká
 * jen na asistenta? U volby „obojí" stačí první hotový výsledek, další se
 * nabídne k porovnání.
 */
export function cekaNaVysledek(receipt: Receipt): boolean {
    return seRozpoznava(receipt) || receipt.analysisStatus === 'AWAITING_MCP'
}

/**
 * Jak daleko je účtenka čekající ve frontě:
 *
 * - `ZPRACOVAVA` - čte ji server, nebo se čeká na AI asistenta. U volby
 *   „obojí" se čeká na oba, aby šly výsledky porovnat dřív, než si lidé
 *   začnou rozebírat položky.
 * - `KE_KONTROLE` - rozpoznaná, čeká na rozebrání položek a potvrzení.
 * - `NEROZPOZNANO` - čtení selhalo, údaje se doplní ručně.
 * - `RUCNE` - nikdo ji nečetl (rozpoznávání vypnuté nebo zrušené čekání
 *   na asistenta), údaje se doplní ručně.
 */
export type StavKontroly = 'ZPRACOVAVA' | 'KE_KONTROLE' | 'NEROZPOZNANO' | 'RUCNE'

export function stavKontroly(receipt: Receipt): StavKontroly {
    if (seRozpoznava(receipt) || cekaNaAsistenta(receipt)) {
        return 'ZPRACOVAVA'
    }
    if (receipt.analysisStatus === 'FAILED') {
        return 'NEROZPOZNANO'
    }
    return receipt.analysisStatus === 'DONE' ? 'KE_KONTROLE' : 'RUCNE'
}

/** Má účtenka víc výsledků rozpoznání, které jde porovnat? */
export function lzePorovnat(receipt: Receipt): boolean {
    return receipt.analysisCount > 1
}

/**
 * Jak často se doptávat na stav účtenek. Rozpoznávání na serveru trvá sekundy
 * až minuty; na asistenta se čeká, než ho uživatel o zpracování požádá, takže
 * stačí řidší dotazy - a po návratu do okna aplikace se stav načte hned.
 */
function intervalDotazu(receipts: Receipt[] | undefined): number | false {
    if (receipts?.some(seRozpoznava)) {
        return 1500
    }
    return receipts?.some(cekaNaAsistenta) ? 10_000 : false
}

export function useTransaction(transactionId: string) {
    return useQuery({
        queryKey: transactionQueryKey(transactionId),
        queryFn: () => actionApiCall(() => getTransaction({ path: { transactionId } })),
        enabled: !!transactionId,
    })
}

export function useReceipts(transactionId: string) {
    return useQuery({
        queryKey: receiptsQueryKey(transactionId),
        queryFn: () => actionApiCall(() => listReceipts({ path: { transactionId } })),
        enabled: !!transactionId,
        // Když na pozadí běží rozpoznávání, stav se doptává, dokud nedoběhne.
        refetchInterval: (query) => intervalDotazu(query.state.data),
        refetchOnWindowFocus: (query) => !!query.state.data?.some(cekaNaAsistenta),
    })
}

/**
 * Fronta účtenek skupiny - nahrané účtenky, které zatím nepatří k žádnému pohybu.
 *
 * Rozpoznávání běží na serveru nezávisle na prohlížeči, takže se fronta po
 * návratu do aplikace jen načte znovu; dokud v ní něco běží, stav se doptává.
 */
export function useReceiptQueue(groupId: string, enabled = true) {
    return useQuery({
        queryKey: receiptQueueQueryKey(groupId),
        queryFn: () => actionApiCall(() => listUnattachedReceipts({ path: { groupId } }), { silent: true }),
        enabled: !!groupId && enabled,
        refetchInterval: (query) => intervalDotazu(query.state.data),
        // Uživatel se z asistenta vrací přepnutím okna - výsledek má být vidět hned.
        refetchOnWindowFocus: (query) => !!query.state.data?.some(cekaNaAsistenta),
    })
}

/**
 * Jedna účtenka - na stránce její kontroly. Dokud ji server čte nebo se čeká
 * na asistenta, stav se doptává stejně jako ve frontě.
 */
export function useReceipt(receiptId: string) {
    return useQuery({
        queryKey: receiptQueryKey(receiptId),
        queryFn: () => actionApiCall(() => getReceipt({ path: { receiptId } }), { silent: true }),
        enabled: !!receiptId,
        // Účtenku mezitím mohl potvrdit nebo upravit někdo jiný - při otevření vždy čerstvá.
        refetchOnMount: 'always',
        refetchInterval: (query) => intervalDotazu(query.state.data ? [query.state.data] : undefined),
        refetchOnWindowFocus: (query) => !!query.state.data && cekaNaAsistenta(query.state.data),
    })
}

export function useInvalidateReceiptQueue() {
    const queryClient = useQueryClient()

    return useCallback(
        (groupId: string) => queryClient.invalidateQueries({ queryKey: receiptQueueQueryKey(groupId) }),
        [queryClient],
    )
}

export function useInvalidateReceipts() {
    const queryClient = useQueryClient()

    return useCallback(
        async (transactionId: string) => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: receiptsQueryKey(transactionId) }),
                queryClient.invalidateQueries({ queryKey: transactionQueryKey(transactionId) }),
            ])
        },
        [queryClient],
    )
}

export const receiptAnalysesQueryKey = (receiptId: string) => ['receipts', 'vysledky', receiptId] as const

/** Uložené výsledky rozpoznání účtenky k porovnání. */
export function useReceiptAnalyses(receiptId: string, enabled = true) {
    return useQuery({
        queryKey: receiptAnalysesQueryKey(receiptId),
        queryFn: () => actionApiCall(() => listReceiptAnalyses({ path: { receiptId } })),
        enabled: !!receiptId && enabled,
        // Výsledky přibývají - při každém otevření porovnání se načtou znovu.
        staleTime: 0,
    })
}
